// tab/src/App.tsx
// OnRounds Phase 2 - Main App Component

import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { FluentProvider, teamsLightTheme, teamsDarkTheme } from '@fluentui/react-components';
import * as microsoftTeams from '@microsoft/teams-js';


// Context
import { UserProvider } from './context/UserContext';
import { TeamProvider } from './context/TeamContext';

// Components
import { Layout } from './components/Layout/Layout';
import { ClockOnOff } from './components/ClockOnOff/ClockOnOff';
import { RoleDirectory } from './components/RoleDirectory/RoleDirectory';
import { MessageCenter } from './components/MessageCenter/MessageCenter';
import { SupervisorView } from './components/SupervisorView/SupervisorView';
import { AuditDashboard } from './components/AuditDashboard/AuditDashboard';
import { AdminPortal } from './components/AdminPortal/AdminPortal';
import { Loading } from './components/common/Loading';
import { ErrorBoundary } from './components/common/ErrorBoundary';

import { api } from './services/api';

const App: React.FC = () => {
  fetch('/api/debug', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message: 'App component started - direct fetch' })
  }).catch(() => {});
  const [isInitialized, setIsInitialized] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeTeams = async () => {
      try {
        // Initialize Teams SDK
        await microsoftTeams.app.initialize();
        
        // Get context
        const context = await microsoftTeams.app.getContext();
        
        // Set theme based on Teams theme
        if (context.app.theme === 'dark') {
          setTheme('dark');
        }

        // Register theme change handler
        microsoftTeams.app.registerOnThemeChangeHandler((newTheme) => {
          setTheme(newTheme === 'dark' ? 'dark' : 'light');
        });

        setIsInitialized(true);
      } catch (err) {
        console.error('Failed to initialize Teams SDK:', err);
        // Allow running outside of Teams for development
        if (process.env.NODE_ENV === 'development') {
          console.log('Running in development mode outside of Teams');
          setIsInitialized(true);
        } else {
          setError('Failed to initialize. Please open this app in Microsoft Teams.');
        }
      }
    };

    initializeTeams();
  }, []);

  if (error) {
    return (
      <FluentProvider theme={teamsLightTheme}>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      </FluentProvider>
    );
  }

  if (!isInitialized) {
    return (
      <FluentProvider theme={teamsLightTheme}>
        <Loading message="Initializing OnRounds..." />
      </FluentProvider>
    );
  }

  return (
    <FluentProvider theme={theme === 'dark' ? teamsDarkTheme : teamsLightTheme}>
      <ErrorBoundary>
        <UserProvider>
          <TeamProvider>
            <BrowserRouter>
              <Layout>
                <Routes>
                  {/* Default route - Clock On/Off */}
                  <Route path="/" element={<ClockOnOff />} />
                  
                  {/* Role Directory */}
                  <Route path="/directory" element={<RoleDirectory />} />
                  
                  {/* Message Center */}
                  <Route path="/messages" element={<MessageCenter />} />
                  <Route path="/messages/:messageId" element={<MessageCenter />} />
                  
                  {/* Supervisor View */}
                  <Route path="/supervisor" element={<SupervisorView />} />
                  
                  {/* Audit Dashboard */}
                  <Route path="/audit" element={<AuditDashboard />} />
                  
                  {/* Admin Portal */}
                  <Route path="/admin" element={<AdminPortal />} />
                  <Route path="/admin/import" element={<AdminPortal />} />
                  <Route path="/admin/roles" element={<AdminPortal />} />
                  <Route path="/admin/users" element={<AdminPortal />} />
                  
                  {/* Fallback */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Layout>
            </BrowserRouter>
          </TeamProvider>
        </UserProvider>
      </ErrorBoundary>
    </FluentProvider>
  );
};

export default App;
